import { useId, useMemo, useState, type ReactElement } from 'react';
import { Badge, Button, Checkbox, SegmentedControl } from '@/components/ui';
import { cx } from '@/lib/cx';
import {
  applyModuleScope,
  applyToggle,
  grantedCount,
  isGranting,
  moduleScopeOf,
  summarise,
} from '@/lib/permission-grid';
import {
  actionLabel,
  OWN_ENFORCED_MODULES,
  sectionOf,
  SECTIONS,
  VIEW_ONLY_MODULES,
  type SectionId,
} from '@/lib/permission-sections';
import type { Permission, PermissionScope } from '@/lib/types';

/*
 * PermissionGrid — the editing surface for ONE permission set.
 *
 * ── Layout (owner decisions 2026-10-02) ────────────────────────────────────
 *
 * Collapsible SECTIONS (Sales, Enquiries, …), several open at once and all open
 * on arrival, each with "N of M granted" and section-level grant-all /
 * clear-all. Each module row shows ONLY the actions the catalogue has for it, as
 * labelled toggles — never a grey placeholder for a combination that does not
 * exist. View-only dashboards/trackers are one "Can see" switch. Modules where
 * the server enforces `own` carry ONE "Records: All / Only theirs" picker; on
 * every other module a grant is written at `all`. The grouping, the labels and
 * both module sets live in `src/lib/permission-sections.ts`.
 *
 * ── Still derived from the catalogue ──────────────────────────────────────
 *
 * Which (module, action) pairs exist is the server's answer from
 * `GET /permissions`, never a list kept here: an unknown module lands in
 * `Other`, an unknown action renders under the server's own name, and the
 * denominator on the count is `catalogue.length`. Order within a section is the
 * catalogue's (`module.sortOrder, action.sortOrder, id`).
 *
 * ── What a tick and an untick WRITE ───────────────────────────────────────
 *
 * Unticking stores an explicit `scope: 'none'`, never a delete; the rules are
 * in `src/lib/permission-grid.ts`, shared with the save confirmation on
 * `PermissionSetDetailPage`. **`value.has(id)` is never the "is this ticked?"
 * test** — a denied cell is in the map; use `isGranting`.
 */

/**
 * The per-module scope picker — GRANTS only. It narrows what is granted and
 * never grants or denies; the toggles decide that.
 */
type ModuleScope = 'all' | 'own' | 'mixed';

const SCOPE_OPTIONS: readonly { value: ModuleScope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'own', label: 'Only theirs' },
];

const SCOPE_TEXT: Record<ModuleScope, string> = {
  all: 'All',
  own: 'Only theirs',
  mixed: 'Mixed',
};

export interface PermissionGridProps {
  /** The full catalogue from GET /permissions. Render FROM THIS. */
  catalogue: Permission[];
  /**
   * Current state, keyed by permission id: `all`/`own` grant, `none` denies,
   * an absent key means the set does not mention the permission (which the
   * server also reads as a denial). Use `isGranting`, never `has(id)`.
   */
  value: ReadonlyMap<number, PermissionScope>;
  onChange: (next: Map<number, PermissionScope>) => void;
  /** Read-only rendering — no inputs, just what is granted. */
  disabled: boolean;
}

interface ModuleRow {
  id: number;
  slug: string;
  name: string;
  /** Every catalogue row of this module, in catalogue order. What a module-wide write touches. */
  all: Permission[];
  /**
   * The rows rendered as toggles and counted. Equal to `all`, except on a
   * view-only module, where it is the `read` row alone (the "Can see" switch).
   */
  shown: Permission[];
  viewOnly: boolean;
  ownEnforced: boolean;
}

interface SectionGroup {
  id: SectionId;
  name: string;
  modules: ModuleRow[];
}

/**
 * Turn the flat catalogue into sections of module rows.
 *
 * First-seen module order is already `module.sortOrder`, and within a module
 * the actions arrive in `action.sortOrder`. A section with no module in the
 * catalogue is not rendered; a module the section map does not know goes to
 * `Other` rather than disappearing.
 */
function buildSections(catalogue: Permission[]): SectionGroup[] {
  const rows: ModuleRow[] = [];
  const rowsById = new Map<number, ModuleRow>();
  const seen = new Set<number>();

  for (const permission of catalogue) {
    if (seen.has(permission.id)) continue;
    seen.add(permission.id);
    let row = rowsById.get(permission.module.id);
    if (!row) {
      row = {
        id: permission.module.id,
        slug: permission.module.slug,
        name: permission.module.name,
        all: [],
        shown: [],
        viewOnly: false,
        ownEnforced: OWN_ENFORCED_MODULES.has(permission.module.slug),
      };
      rowsById.set(row.id, row);
      rows.push(row);
    }
    row.all.push(permission);
  }

  for (const row of rows) {
    const read = row.all.find((permission) => permission.action.slug === 'read');
    row.viewOnly = VIEW_ONLY_MODULES.has(row.slug) && read !== undefined;
    row.shown = row.viewOnly && read ? [read] : row.all;
  }

  return SECTIONS.map((section) => ({
    id: section.id,
    name: section.name,
    modules: rows.filter((row) => sectionOf(row.slug) === section.id),
  })).filter((section) => section.modules.length > 0);
}

const idsOf = (permissions: readonly Permission[]): number[] =>
  permissions.map((permission) => permission.id);

/** "Edit on Deals" — a bare toggle is unusable with a screen reader. */
function cellName(row: ModuleRow, permission: Permission): string {
  return `${actionLabel(permission.action.slug, permission.action.name)} on ${row.name}`;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx(
        'h-4 w-4 shrink-0 text-fg-muted transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-90',
      )}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export function PermissionGrid({
  catalogue,
  value,
  onChange,
  disabled,
}: PermissionGridProps): ReactElement {
  const sections = useMemo(() => buildSections(catalogue), [catalogue]);
  const counts = useMemo(() => summarise(value), [value]);
  const baseId = useId();

  /* Collapsed sections. Every section starts OPEN on this page: the editor is
     where a set is reviewed end to end, and a closed section hides grants. */
  const [collapsed, setCollapsed] = useState<ReadonlySet<SectionId>>(() => new Set());

  function toggleSection(id: SectionId): void {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function scopeOf(row: ModuleRow): ModuleScope {
    return moduleScopeOf(value, idsOf(row.all));
  }

  /**
   * What a tick writes on this module. No picker → always `all`. With a
   * picker → the module's scope, so a newly ticked action joins the others;
   * on legacy mixed data, `undefined` preserves each cell's own scope.
   */
  function grantScopeFor(row: ModuleRow): 'all' | 'own' | undefined {
    if (!row.ownEnforced) return 'all';
    const scope = scopeOf(row);
    return scope === 'mixed' ? undefined : scope;
  }

  function toggleOne(row: ModuleRow, permissionId: number, on: boolean): void {
    onChange(applyToggle(value, [permissionId], on, grantScopeFor(row)));
  }

  /* The "Can see" switch writes every row of the module, inert leftovers
     included, so nothing stays granted under a switch that reads off. */
  function toggleModule(row: ModuleRow, on: boolean): void {
    onChange(applyToggle(value, idsOf(row.all), on, grantScopeFor(row)));
  }

  function toggleSectionAll(section: SectionGroup, on: boolean): void {
    let next: Map<number, PermissionScope> = new Map(value);
    for (const row of section.modules) {
      next = applyToggle(next, idsOf(row.all), on, grantScopeFor(row));
    }
    onChange(next);
  }

  function setModuleScope(row: ModuleRow, scope: 'all' | 'own'): void {
    onChange(applyModuleScope(value, idsOf(row.all), scope));
  }

  function renderActions(row: ModuleRow): ReactElement {
    if (disabled) {
      return (
        <ul className="flex flex-wrap gap-1.5">
          {row.shown.map((permission) => {
            const granted = isGranting(value.get(permission.id));
            const label = row.viewOnly
              ? 'Can see'
              : actionLabel(permission.action.slug, permission.action.name);
            return (
              <li key={permission.id}>
                {granted ? (
                  <Badge tone="success">{label}</Badge>
                ) : (
                  <span className="inline-flex items-center rounded-sm border border-border px-1.5 py-0.5 text-xs text-fg-subtle line-through">
                    {label}
                    <span className="sr-only">: not granted</span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      );
    }

    if (row.viewOnly) {
      const read = row.shown[0];
      return (
        <Checkbox
          checked={read !== undefined && isGranting(value.get(read.id))}
          onChange={(event) => toggleModule(row, event.target.checked)}
          label={
            <span>
              <span aria-hidden="true">Can see</span>
              <span className="sr-only">Can see {row.name}</span>
            </span>
          }
        />
      );
    }

    return (
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {row.shown.map((permission) => (
          <Checkbox
            key={permission.id}
            checked={isGranting(value.get(permission.id))}
            onChange={(event) => toggleOne(row, permission.id, event.target.checked)}
            label={
              <span>
                <span aria-hidden="true">
                  {actionLabel(permission.action.slug, permission.action.name)}
                </span>
                <span className="sr-only">{cellName(row, permission)}</span>
              </span>
            }
          />
        ))}
      </div>
    );
  }

  function renderScope(row: ModuleRow): ReactElement | null {
    if (!row.ownEnforced || grantedCount(value, idsOf(row.all)) === 0) return null;
    const scope = scopeOf(row);

    return (
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-xs font-medium text-fg-muted">Records</span>
        {disabled ? (
          <span className="text-xs text-fg">{SCOPE_TEXT[scope]}</span>
        ) : (
          <div className="w-full max-w-60 sm:w-60">
            <SegmentedControl<ModuleScope>
              label={`Records ${row.name} grants reach`}
              /* Calmer than the default tray: this control repeats on every
                 own-enforced module, so the sunken fill made a column of heavy
                 bars. The selected option still carries the secondary button's
                 outline and `aria-pressed`, so the state never rests on fill.
                 ⚠️ `!` (important) is load-bearing: `cx` does not merge, and two
                 background utilities resolve by stylesheet order, not by the
                 order written — without it the tray could win silently. */
              className="bg-transparent!"
              value={scope}
              options={SCOPE_OPTIONS}
              onChange={(next) => {
                if (next !== 'mixed') setModuleScope(row, next);
              }}
            />
          </div>
        )}
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <p className="rounded-md border border-border bg-surface px-3 py-6 text-center text-sm text-fg-muted">
        The permission catalogue is empty.
      </p>
    );
  }

  return (
    /* min-w-0: a grid or flex child defaults to min-width:auto; this refuses to
       be widened by its children, so the page never scrolls sideways at 320px. */
    <div className="min-w-0 space-y-3">
      {/* GRANTS, not rows. Unticking stores a denial, so `value.size` says
          nothing about access — a set can hold every catalogue row and grant
          none. The explicit-deny badge went: every seeded set now carries a
          row per permission, so it would only restate "not granted". */}
      <p aria-live="polite" className="text-sm text-fg">
        <span className="font-medium tabular">{counts.granting}</span>
        <span className="text-fg-muted"> of {catalogue.length} permissions granted</span>
      </p>

      {sections.map((section) => {
        const open = !collapsed.has(section.id);
        const counted = section.modules.flatMap((row) => idsOf(row.shown));
        const granted = grantedCount(value, counted);
        const panelId = `${baseId}-${section.id}`;

        return (
          <section key={section.id} className="rounded-md border border-border bg-surface">
            <header
              className={cx(
                'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 py-2',
                open && 'border-b border-border',
              )}
            >
              <h3 className="m-0 min-w-0">
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => toggleSection(section.id)}
                  className="flex min-w-0 items-center gap-2 rounded-sm text-left focus-visible:outline-2 focus-visible:outline-focus-ring"
                >
                  <Chevron open={open} />
                  <span className="truncate text-sm font-semibold text-fg">{section.name}</span>
                  <span className="text-xs whitespace-nowrap text-fg-muted tabular">
                    {granted} of {counted.length} granted
                  </span>
                </button>
              </h3>
              {!disabled && (
                <div className="flex shrink-0 gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={granted === counted.length}
                    onClick={() => toggleSectionAll(section, true)}
                    aria-label={`Grant every ${section.name} permission`}
                  >
                    Grant all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={granted === 0}
                    onClick={() => toggleSectionAll(section, false)}
                    aria-label={`Clear every ${section.name} permission`}
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </header>

            {open && (
              <ul id={panelId} className="divide-y divide-border">
                {section.modules.map((row) => (
                  <li
                    key={row.id}
                    className="flex min-w-0 flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-start sm:gap-4"
                  >
                    <span className="min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere] sm:w-56 sm:shrink-0 sm:pt-0.5">
                      {row.name}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      {renderActions(row)}
                      {renderScope(row)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
